import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, BookOpen, Clock } from "react-feather";
import { useLocation } from "react-router-dom";
import {
    createOrdersFromCart,
  fetchUserOrders,
} from "../../../features/orderSlice";
import { fetchUserAddress } from "../../../features/addressSlice";
import { fetchCurrentSubscription } from "../../../features/subscriptionSlice";
import BookManager from "../../../services/home/BookManager";
import CityManager from "../../../services/home/CityManager";
import AddAddressModal from "../addressComponents/AddAddressModal";
import config from "../../../config";
import { useMemo } from "react";

const MultiBookOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const bookIdsParam = searchParams.get("bookIds");
  const selectedBookIds = useMemo(() => {
    return bookIdsParam ? bookIdsParam.split(",") : [];
  }, [bookIdsParam]);

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const userId = user?.userId;
  const { userOrders } = useSelector((state) => state.orders);
  const { subscription } = useSelector((state) => state.subscription);
  const { address: rawAddress } = useSelector((state) => state.address);

  const [bookDetailsList, setBookDetailsList] = useState([]);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [order, setOrder] = useState(null);
  const [allowedBookLimit, setAllowedBookLimit] = useState(0);
  const [remainingBooks, setRemainingBooks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);

  useEffect(() => {
    if (!userId || !bookIdsParam) return;

    const init = async () => {
      try {
        // 1. Fetch Cities
        let cities = [];
        try {
          cities = await CityManager.getActiveCitiesWithPincodes();
          setCityOptions(cities.map((c) => ({ id: c.cityId, name: c.cityName })));
          const allPins = cities.flatMap((c) =>
            c.pincodes.map((p) => ({ id: p.pinId, code: p.pinCode }))
          );
          setPincodeOptions(allPins);
        } catch (err) {
          console.error("Error fetching cities:", err);
          toast.error("Failed to load city data.");
          return;
        }

        // 2. Fetch Address
        try {
          await dispatch(fetchUserAddress(userId)).unwrap();
        } catch (err) {
          console.error("Error fetching user address:", err);
          toast.error("Failed to load user address.");
          return;
        }
  
        // 3. Fetch Subscription
        let latestSub;
        try {
          latestSub = await dispatch(fetchCurrentSubscription(userId)).unwrap();
        } catch (err) {
          console.error("Error fetching subscription:", err);
          toast.error("Failed to load subscription.");
          return;
        }

        // 4. Fetch Orders
        let orders = [];
        try {
          orders = await dispatch(fetchUserOrders(userId)).unwrap();
        } catch (err) {
          console.error("Error fetching user orders:", err);
          toast.error("Failed to load order history.");
          return;
        }

        // 5. Fetch Book Details
        let bookDetails = [];
        try {
          bookDetails = await Promise.all(
            selectedBookIds.map(async (id) => {
              try {
                const book = await BookManager.getBookById(id);
                return {
                  id,
                  title: book.bookName,
                  images: book.bookImages?.map((img) => ({ url: img })) || [],
                  author: { name: book.author?.authorName || "Unknown" },
                  category: { name: book.category?.catName || "N/A" },
                  description: book.bookDescription,
                };
              } catch (err) {
                console.error(`Error fetching book ID ${id}:`, err);
                return null;
              }
            })
          );
          setBookDetailsList(bookDetails.filter((b) => b !== null));
        } catch (err) {
          console.error("Error loading book details list:", err);
          toast.error("Failed to load book details.");
          return;
        }

        // 6. Subscription Limits
        const limitMap = {
          ONE_BOOK: 1,
          TWO_BOOKS: 2,
          THREE_BOOKS: 3,
        };
        const bookLimit = limitMap[latestSub?.subscription?.packageType] || 0;
        const activeOrders = (orders || []).filter(
          (o) => o.returnStatus === "NOT_RETURNED"
        ).length;

        setAllowedBookLimit(bookLimit);
        setRemainingBooks(bookLimit - activeOrders);
      } catch (err) {
        console.error("Unexpected MultiBookOrder INIT error:", err);
        toast.error("Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [userId, bookIdsParam, selectedBookIds, dispatch, navigate]);


  const handleOrder = async () => {
    if (!subscription || subscription.status !== "ACTIVE") {
      toast.error("You need an active subscription.");
      navigate("/membershipPlans");
      return;
    }

    if (!rawAddress) {
      toast.error("Please add a delivery address.");
      return;
    }

    try {
      setLoading(true);
      // Dispatch the entire array of bookIds, not a single id
      const orderResult = await dispatch(createOrdersFromCart({ userId, bookIds: selectedBookIds })).unwrap();
      setOrder(orderResult);
      toast.success("Order placed successfully!");
      setOrderPlaced(true);
    } catch (err) {
      toast.error(err.message || "Failed to place order.");
    } finally {
      setLoading(false);
    }
  };


  if (loading) return <div className="p-4">Loading details...</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <h2 className="text-3xl font-bold">Confirm Your Order</h2>
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Book Cards */}
        <div className="lg:col-span-2 space-y-6">
          {bookDetailsList.map((book) => (
            <div key={book.id} className="bg-white rounded-xl p-6 shadow-md flex gap-4">
              <img
                src={`${config.url}${book.images[0]?.url}`}
                className="w-28 h-44 object-cover rounded-lg"
                alt={book.title}
              />
              <div className="space-y-1">
                <h3 className="text-xl font-semibold">{book.title}</h3>
                <p className="text-text-muted flex items-center gap-2">
                  <BookOpen size={16} className="text-primary" />
                  {book.author.name}
                </p>
                <p className="text-sm text-gray-500">{book.description}</p>
              </div>
            </div>
          ))}

          {/* Address Block */}
          <div className="bg-white p-4 rounded-xl shadow-md">
            {rawAddress ? (
              <>
                <p>{rawAddress.fullAddress}</p>
                <p>
                  {rawAddress.city.cityName}, {rawAddress.city.state}{" "}
                  {rawAddress.pincode.pinCode}
                </p>
                <button
                  onClick={() => setModalOpen(true)}
                  className="text-primary underline"
                >
                  Edit Address
                </button>
              </>
            ) : (
              <button
                onClick={() => setModalOpen(true)}
                className="text-red-500 underline"
              >
                Add Delivery Address
              </button>
            )}
          </div>

          <AddAddressModal
            isOpen={isModalOpen}
            onClose={() => setModalOpen(false)}
            userId={userId}
            cityOptions={cityOptions}
            pincodeOptions={pincodeOptions}
            initialData={
              rawAddress
                ? {
                    fullAddress: rawAddress.fullAddress,
                    cityId: rawAddress.city.cityId,
                    pincodeId: rawAddress.pincode.pincodeId,
                  }
                : null
            }
          />
        </div>

        {/* Order Summary */}
        <div className="bg-white p-6 rounded-xl shadow-md sticky top-6 space-y-4">
          <AnimatePresence mode="wait">
            {orderPlaced ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                <h3 className="text-xl text-center font-bold">
                  Order Confirmed!
                </h3>
                <button
                  onClick={() => navigate("/myOrders")}
                  className="w-full bg-primary text-white py-2 rounded-lg mt-4"
                >
                  View My Orders
                </button>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h3 className="text-xl font-bold">Order Summary</h3>
                <p>
                  Books Selected:{" "}
                  <strong>
                    {selectedBookIds.length} / {remainingBooks}
                  </strong>
                </p>
                <p>
                  Plan: <strong>{subscription?.planName}</strong>
                </p>
                <p>
                  Package:{" "}
                  <strong>
                    {subscription?.packageType?.replace(/_/g, " ")}
                  </strong>
                </p>
                <button
                  onClick={handleOrder}
                  className="w-full bg-primary text-white py-3 rounded-lg mt-4 hover:bg-primary-dark"
                >
                  {loading ? "Placing Order..." : "Confirm Order"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default MultiBookOrder;
